import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  TrendingUp, TrendingDown, DollarSign, Target, Calendar, 
  ArrowUp, ArrowDown, Minus, PieChart, BarChart3, Settings,
  RefreshCw, Receipt, Building2, Hotel
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatPercent = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

export default function BudgetDashboard({ user, selectedCompany }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Check if user is Company Admin or Hotel Admin
  const isCompanyAdmin = user?.role === 'company_admin' || user?.role === 'super_admin' || user?.role === 'finance_admin';
  const isHotelAdmin = user?.role === 'hotel_admin';
  const [companySummary, setCompanySummary] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedHotel, setSelectedHotel] = useState(null); // null = company view
  const [forecastHorizon, setForecastHorizon] = useState('quarter');
  const [viewMode, setViewMode] = useState('company'); // 'company' or 'hotel'
  
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const hotelParam = viewMode === 'hotel' && selectedHotel ? `&hotel_id=${selectedHotel}` : '';
      
      const [dashboardRes, summaryRes, forecastRes] = await Promise.all([
        api.get(`/budget/dashboard?year=${selectedYear}${hotelParam}`),
        api.get(`/budget/company-summary?year=${selectedYear}&month=${selectedMonth}`),
        api.get(`/budget/forecast?horizon=${forecastHorizon}${hotelParam}`)
      ]);
      
      setDashboardData(dashboardRes.data);
      setCompanySummary(summaryRes.data);
      setForecastData(forecastRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load budget data'));
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedHotel, viewMode, forecastHorizon]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-green-600';
    if (variance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <ArrowUp className="h-3 w-3" />;
    if (variance < 0) return <ArrowDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  const getProfitColor = (profit) => {
    if (profit > 0) return 'text-green-600 bg-green-50';
    if (profit < 0) return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-6" data-testid="budget-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-8 w-8 text-teal-500" />
            Budget Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Track revenue, expenditure, and forecasts</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View Mode Toggle - Only for Company Admin */}
          {isCompanyAdmin && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <Button 
                variant={viewMode === 'company' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => { setViewMode('company'); setSelectedHotel(null); }}
                className={viewMode === 'company' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              >
                <Building2 className="h-4 w-4 mr-1" />
                Company
              </Button>
              <Button 
                variant={viewMode === 'hotel' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('hotel')}
                className={viewMode === 'hotel' ? 'bg-teal-600 hover:bg-teal-700' : ''}
              >
                <Hotel className="h-4 w-4 mr-1" />
                Hotel
              </Button>
            </div>
          )}
          
          {/* Hotel Admin Label */}
          {isHotelAdmin && (
            <Badge variant="outline" className="text-teal-700 border-teal-300 bg-teal-50">
              <Hotel className="h-3 w-3 mr-1" />
              {dashboardData?.hotels?.[0]?.name || 'My Hotel'}
            </Badge>
          )}
          
          {/* Hotel Selector (only in hotel mode for Company Admin) */}
          {isCompanyAdmin && viewMode === 'hotel' && dashboardData?.hotels?.length > 0 && (
            <Select value={selectedHotel || 'all'} onValueChange={(v) => setSelectedHotel(v === 'all' ? null : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select Hotel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hotels</SelectItem>
                {dashboardData.hotels.map(hotel => (
                  <SelectItem key={hotel.id} value={hotel.id}>{hotel.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((month, idx) => (
                <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {/* Budget Setup - Only for Company Admin */}
          {isCompanyAdmin && (
            <Button onClick={() => navigate('/admin/budget/setup')} className="bg-teal-600 hover:bg-teal-700">
              <Settings className="h-4 w-4 mr-2" />
              Budget Setup
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Current Month Summary - Key Metrics */}
          {companySummary && (
            <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-teal-500" />
                  {MONTHS[selectedMonth - 1]} {selectedYear} - {viewMode === 'company' ? 'Company' : 'Hotel'} Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Expected Expenditure</p>
                    <p className="text-xl font-bold text-red-600">
                      {formatCurrency(companySummary.company_totals?.expected_expenditure)}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Actual Expenditure</p>
                    <p className="text-xl font-bold text-red-700">
                      {formatCurrency(companySummary.company_totals?.actual_expenditure)}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Current Revenue</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(companySummary.company_totals?.current_revenue)}
                    </p>
                  </div>
                  <div className="p-3 bg-white rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Forecast Revenue</p>
                    <p className="text-xl font-bold text-blue-600">
                      {formatCurrency(companySummary.company_totals?.forecast_revenue)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg shadow-sm ${getProfitColor(companySummary.company_totals?.expected_profit_loss)}`}>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Expected P/L</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(companySummary.company_totals?.expected_profit_loss)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg shadow-sm ${getProfitColor(companySummary.company_totals?.current_profit_loss)}`}>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Current P/L</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(companySummary.company_totals?.current_profit_loss)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* YTD Summary Cards */}
          {dashboardData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Revenue (YTD)</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(dashboardData.ytd?.revenue)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Expenditure (YTD)</p>
                      <p className="text-2xl font-bold text-red-600">
                        {formatCurrency(dashboardData.ytd?.actual_expenditure)}
                      </p>
                    </div>
                    <TrendingDown className="h-8 w-8 text-red-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Net Profit (YTD)</p>
                      <p className={`text-2xl font-bold ${dashboardData.ytd?.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(dashboardData.ytd?.profit)}
                      </p>
                    </div>
                    <Target className="h-8 w-8 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Budget Variance</p>
                      <p className={`text-2xl font-bold ${getVarianceColor(dashboardData.ytd?.variance)}`}>
                        {formatCurrency(Math.abs(dashboardData.ytd?.variance || 0))}
                      </p>
                      <p className={`text-xs flex items-center gap-1 ${getVarianceColor(dashboardData.ytd?.variance)}`}>
                        {getVarianceIcon(dashboardData.ytd?.variance)}
                        {dashboardData.ytd?.variance >= 0 ? 'Under Budget' : 'Over Budget'}
                      </p>
                    </div>
                    <PieChart className="h-8 w-8 text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Budget Utilization Bar */}
          {dashboardData && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Budget Utilization (YTD)</span>
                  <span className="text-sm font-semibold">
                    {formatPercent(dashboardData.summary?.budget_utilization_percent)}
                  </span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      dashboardData.summary?.budget_utilization_percent > 100 
                        ? 'bg-red-500' 
                        : dashboardData.summary?.budget_utilization_percent > 80 
                          ? 'bg-amber-500' 
                          : 'bg-teal-500'
                    }`}
                    style={{ width: `${Math.min(100, dashboardData.summary?.budget_utilization_percent || 0)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Spent: {formatCurrency(dashboardData.ytd?.actual_expenditure)}</span>
                  <span>Budget: {formatCurrency(dashboardData.ytd?.budgeted_expenditure)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs for different views */}
          <Tabs defaultValue="hotels" className="space-y-4">
            <TabsList>
              {viewMode === 'company' && <TabsTrigger value="hotels">Hotel Breakdown</TabsTrigger>}
              <TabsTrigger value="monthly">Monthly Trend</TabsTrigger>
              <TabsTrigger value="forecast">Forecast</TabsTrigger>
            </TabsList>

            {/* Hotel Breakdown (Company View) */}
            {viewMode === 'company' && (
              <TabsContent value="hotels">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-teal-500" />
                      Hotel-wise Budget Summary - {MONTHS[selectedMonth - 1]} {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {companySummary?.hotel_breakdown?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Hotel</TableHead>
                              <TableHead className="text-right">Expected Exp.</TableHead>
                              <TableHead className="text-right">Actual Exp.</TableHead>
                              <TableHead className="text-right">Current Revenue</TableHead>
                              <TableHead className="text-right">Forecast Revenue</TableHead>
                              <TableHead className="text-right">Expected P/L</TableHead>
                              <TableHead className="text-right">Current P/L</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {companySummary.hotel_breakdown.map((hotel) => (
                              <TableRow key={hotel.hotel_id} className="hover:bg-gray-50">
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <Hotel className="h-4 w-4 text-gray-400" />
                                    {hotel.hotel_name}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right text-gray-600">
                                  {formatCurrency(hotel.expected_expenditure)}
                                </TableCell>
                                <TableCell className="text-right text-red-600">
                                  {formatCurrency(hotel.actual_expenditure)}
                                </TableCell>
                                <TableCell className="text-right text-green-600">
                                  {formatCurrency(hotel.current_revenue)}
                                </TableCell>
                                <TableCell className="text-right text-blue-600">
                                  {formatCurrency(hotel.forecast_revenue)}
                                </TableCell>
                                <TableCell className={`text-right font-semibold ${hotel.expected_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(hotel.expected_profit)}
                                </TableCell>
                                <TableCell className={`text-right font-semibold ${hotel.current_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(hotel.current_profit)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No hotel data available. Set up budget allocations for hotels first.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Monthly Trend */}
            <TabsContent value="monthly">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-teal-500" />
                    Monthly Budget vs Actual - {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead className="text-right">Revenue</TableHead>
                          <TableHead className="text-right">Budgeted</TableHead>
                          <TableHead className="text-right">Actual</TableHead>
                          <TableHead className="text-right">Variance</TableHead>
                          <TableHead className="text-right">Profit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboardData?.monthly?.map((month, idx) => (
                          <TableRow key={idx} className={idx === dashboardData.current_month - 1 ? 'bg-teal-50' : ''}>
                            <TableCell className="font-medium">
                              {MONTHS[month.month - 1]}
                              {idx === dashboardData.current_month - 1 && (
                                <Badge variant="outline" className="ml-2 text-xs">Current</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-green-600">
                              {formatCurrency(month.revenue)}
                            </TableCell>
                            <TableCell className="text-right text-gray-600">
                              {formatCurrency(month.budgeted_expenditure)}
                            </TableCell>
                            <TableCell className="text-right text-red-600">
                              {formatCurrency(month.actual_expenditure)}
                            </TableCell>
                            <TableCell className={`text-right ${getVarianceColor(month.variance)}`}>
                              <span className="flex items-center justify-end gap-1">
                                {getVarianceIcon(month.variance)}
                                {formatCurrency(Math.abs(month.variance))}
                              </span>
                            </TableCell>
                            <TableCell className={`text-right font-semibold ${month.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(month.profit)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Forecast */}
            <TabsContent value="forecast">
              <div className="space-y-4">
                {/* Forecast Horizon Selector */}
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">Forecast Horizon:</span>
                  <Select value={forecastHorizon} onValueChange={setForecastHorizon}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="quarter">This Quarter</SelectItem>
                      <SelectItem value="year">This Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Forecast Summary Cards */}
                {forecastData && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                        <CardContent className="p-4">
                          <p className="text-sm text-green-700">Projected Revenue</p>
                          <p className="text-2xl font-bold text-green-800">
                            {formatCurrency(forecastData.total_projected_revenue)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                        <CardContent className="p-4">
                          <p className="text-sm text-red-700">Projected Expenditure</p>
                          <p className="text-2xl font-bold text-red-800">
                            {formatCurrency(forecastData.total_projected_expenditure)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                        <CardContent className="p-4">
                          <p className="text-sm text-blue-700">Projected Profit</p>
                          <p className={`text-2xl font-bold ${forecastData.total_projected_profit >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                            {formatCurrency(forecastData.total_projected_profit)}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Forecast Details Table */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <BarChart3 className="h-5 w-5 text-teal-500" />
                          Forecast Details (Based on Confirmed Bookings)
                        </CardTitle>
                        <CardDescription>
                          Variable costs calculated based on projected occupancy
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Month</TableHead>
                              <TableHead className="text-right">Bookings</TableHead>
                              <TableHead className="text-right">Room Nights</TableHead>
                              <TableHead className="text-right">Revenue</TableHead>
                              <TableHead className="text-right">Fixed Costs</TableHead>
                              <TableHead className="text-right">Variable Costs</TableHead>
                              <TableHead className="text-right">Profit</TableHead>
                              <TableHead className="text-right">Margin</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {forecastData.forecast?.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">
                                  {MONTHS[item.month - 1]} {item.year}
                                </TableCell>
                                <TableCell className="text-right">{item.bookings_count}</TableCell>
                                <TableCell className="text-right">{item.projected_room_nights}</TableCell>
                                <TableCell className="text-right text-green-600">
                                  {formatCurrency(item.projected_revenue)}
                                </TableCell>
                                <TableCell className="text-right text-gray-600">
                                  {formatCurrency(item.fixed_expenditure)}
                                </TableCell>
                                <TableCell className="text-right text-amber-600">
                                  {formatCurrency(item.variable_expenditure)}
                                </TableCell>
                                <TableCell className={`text-right font-semibold ${item.projected_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(item.projected_profit)}
                                </TableCell>
                                <TableCell className={`text-right ${item.profit_margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatPercent(item.profit_margin)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Quick Actions - Only for Company Admin */}
          {isCompanyAdmin && (
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => navigate('/admin/budget/setup')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configure Budget Categories
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/budget/allocations')}>
                    <Building2 className="h-4 w-4 mr-2" />
                    Hotel Budget Allocations
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Data Source Info */}
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 flex items-center gap-2">
                <Receipt className="h-3 w-3" />
                <span><strong>Data Source:</strong> Actual Expenditure from Finance Module (Purchase Invoices) • Revenue from Bookings</span>
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
